import { z } from "zod";

export const browserInfoSchema = z.object({
  color_depth: z.number().optional(),
  screen_height: z.number().optional(),
  screen_width: z.number().optional(),
  time_zone: z.number().optional(),
  java_enabled: z.boolean().optional(),
  java_script_enabled: z.boolean().optional(),
  language: z.string().optional(),
  user_agent: z.string().optional(),
  accept_header: z.string().optional(),
});

export type BrowserInfo = z.infer<typeof browserInfoSchema>;

export function getBrowserInfo(): BrowserInfo | undefined {
  // Check if we're in a browser environment
  if (typeof window === "undefined") {
    console.warn("getBrowserInfo called in non-browser environment");
    return undefined;
  }

  try {
    const { screen, navigator } = window;

    // Validate that we have the required objects
    if (!screen || !navigator) {
      console.warn("Missing screen or navigator objects");
      return undefined;
    }

    const browserInfo: BrowserInfo = {
      color_depth: screen.colorDepth || undefined,
      screen_height: screen.height || undefined,
      screen_width: screen.width || undefined,
      time_zone: new Date().getTimezoneOffset(),
      java_enabled:
        typeof navigator.javaEnabled === "function"
          ? navigator.javaEnabled()
          : false,
      java_script_enabled: true,
      language: navigator.language || undefined,
      user_agent: navigator.userAgent || undefined,
      accept_header: "*/*",
    };

    // Validate that we have at least some essential info
    if (
      !browserInfo.screen_height ||
      !browserInfo.screen_width ||
      !browserInfo.user_agent
    ) {
      console.warn("Missing essential browser info fields");
    }

    return browserInfo;
  } catch (error) {
    console.error("Error getting browser info:", error);
    return undefined;
  }
}
